// src/context/AuthContext.js
import React, { createContext, useState, useEffect } from "react";
import { jwtDecode } from "jwt-decode";
import axiosInstance from "../utils/axiosInstance";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(null);

  const getStoredTokens = () => {
    const accessToken = sessionStorage.getItem("accessToken");
    const refreshToken = sessionStorage.getItem("refreshToken");
    return { accessToken, refreshToken };
  };

  const saveTokens = (accessToken, refreshToken) => {
    sessionStorage.setItem("accessToken", accessToken);
    sessionStorage.setItem("refreshToken", refreshToken);
  };

  const clearTokens = () => {
    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("refreshToken");
  };

  const logout = () => {
    clearTokens();
    setAuth(null);
  };

  const login = (accessToken, refreshToken) => {
    try {
      const decoded = jwtDecode(accessToken);
      saveTokens(accessToken, refreshToken);
      setAuth({ token: accessToken, user: decoded });
    } catch (error) {
      console.error("Login token decode failed:", error);
    }
  };

  const refreshAccessToken = async () => {
    try {
      const { refreshToken } = getStoredTokens();
      if (!refreshToken) throw new Error("No refresh token available");

      const response = await axiosInstance.post("/api/auth/refresh", { refreshToken });
      const { accessToken } = response.data;

      const decoded = jwtDecode(accessToken);
      sessionStorage.setItem("accessToken", accessToken);
      setAuth({ token: accessToken, user: decoded });
    } catch (err) {
      console.error("Failed to refresh token:", err);
      logout();
    }
  };

  useEffect(() => {
    const { accessToken } = getStoredTokens();
    if (accessToken) {
      try {
        const decoded = jwtDecode(accessToken);
        if (decoded.exp * 1000 < Date.now()) {
          // Access token expired – try refreshing
          refreshAccessToken();
        } else {
          setAuth({ token: accessToken, user: decoded });
        }
      } catch (err) {
        console.error("Invalid access token:", err);
        logout();
      }
    }
  }, []);

  const isAuthenticated = () => auth !== null;
  const isSuperAdmin = () => auth?.user?.role === "superadmin";
  const isAdmin = () => auth?.user?.role === "admin1" || auth?.user?.role === "admin2";
  const isUser = () => auth?.user?.role === "user";

  return (
    <AuthContext.Provider
      value={{
        auth,
        login,
        logout,
        isAuthenticated,
        isSuperAdmin,
        isAdmin,
        isUser,
        refreshAccessToken, // exposed if needed manually
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
